import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import adminApiService from '../../services/adminApiService';
import { toast } from 'react-toastify';

const AdminAgentTokenRecharge = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [tokensToGrant, setTokensToGrant] = useState('20');
  const [grantReason, setGrantReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAgentTokenRechargeData();
    fetchStatistics();
  }, []);

  const fetchAgentTokenRechargeData = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getAllAgentTokenRechargeDetails();
      if (response.success) {
        setAgents(response.data);
      } else {
        toast.error(response.message || 'Failed to fetch agent token data');
      }
    } catch (error) {
      console.error('Error fetching agent token data:', error);
      toast.error('Failed to fetch agent token data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await adminApiService.getTokenRechargeStatistics();
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const openAllocateModal = (agent) => {
    setSelectedAgent(agent);
    setTokensToGrant('20');
    setGrantReason('Extra token allocation by Admin');
    setShowModal(true);
  };

  const handleGrantTokens = async (e) => {
    e.preventDefault();
    if (!selectedAgent || !tokensToGrant || parseInt(tokensToGrant) <= 0) {
      toast.error('Please enter a valid number of tokens to allocate');
      return;
    }

    try {
      setSubmitting(true);
      const response = await adminApiService.grantExtraTokens(selectedAgent.agentId, {
        tokens: parseInt(tokensToGrant),
        reason: grantReason.trim() || 'Extra tokens granted by Admin'
      });

      if (response.success) {
        toast.success(`Successfully allocated ${tokensToGrant} tokens to Agent ${selectedAgent.agentId}!`);
        setShowModal(false);
        await fetchAgentTokenRechargeData();
        await fetchStatistics();
      } else {
        toast.error(response.message || 'Failed to allocate tokens');
      }
    } catch (error) {
      console.error('Error allocating tokens:', error);
      toast.error('Failed to allocate tokens');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Token Management & Audit Ledger</h1>
          <p className="text-gray-600 mt-1">View token accounts, audit transaction ledgers, and manually allocate extra tokens</p>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-2xl font-bold text-blue-600">{statistics.totalAgents}</div>
            <div className="text-sm text-blue-800">Total Agents</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-600">{statistics.totalTokens}</div>
            <div className="text-sm text-green-800">Total Token Balance</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="text-2xl font-bold text-orange-600">{statistics.activeAgents}</div>
            <div className="text-sm text-orange-800">Active Agent Accounts</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-2xl font-bold text-purple-600">
              {statistics.averageTokensPerAgent ? statistics.averageTokensPerAgent.toFixed(1) : 0}
            </div>
            <div className="text-sm text-purple-800">Avg Tokens/Agent</div>
          </div>
        </div>
      )}

      {/* Agents Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Agent Token Accounts</h2>
        </div>
        
        {agents && agents.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Tokens</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tokens Used</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {agents.map((agent, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link 
                        to={`/admin/dashboard/agent-token-recharge/${agent.agentId}/token-recharge-history`}
                        className="text-blue-600 hover:text-blue-900 font-medium font-mono text-sm"
                      >
                        {agent.agentId || 'N/A'}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {agent.email || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {`${agent.firstName || ''} ${agent.lastName || ''}`.trim() || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {agent.phoneNumber || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                        agent.tokens > 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {agent.tokens || 0} tokens
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                      <span className="inline-flex px-2.5 py-1 text-xs font-bold bg-orange-100 text-orange-800 rounded-full">
                        {agent.usedTokens || 0} used
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => openAllocateModal(agent)}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors duration-200 inline-flex items-center"
                      >
                        <i className="fas fa-coins mr-1"></i>
                        Allocate Tokens
                      </button>
                      <Link
                        to={`/admin/dashboard/agent-token-recharge/${agent.agentId}/token-recharge-history`}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 inline-flex items-center"
                      >
                        <i className="fas fa-history mr-1"></i>
                        Audit Ledger
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-8 text-center text-gray-500">
            No agents found for token management.
          </div>
        )}
      </div>

      {/* Allocate Extra Tokens Modal */}
      {showModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4 border-b pb-3">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <i className="fas fa-coins text-green-600 mr-2"></i>
                Allocate Tokens - {selectedAgent.agentId}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>

            <form onSubmit={handleGrantTokens} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name & Email</label>
                <div className="text-sm bg-gray-50 p-2.5 rounded-lg border font-medium">
                  {selectedAgent.firstName} {selectedAgent.lastName} ({selectedAgent.email})
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Token Balance</label>
                <div className="text-sm font-bold text-blue-900 bg-blue-50 p-2.5 rounded-lg border border-blue-100">
                  {selectedAgent.tokens || 0} tokens
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Tokens to Grant *</label>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  required
                  value={tokensToGrant}
                  onChange={(e) => setTokensToGrant(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm font-medium"
                  placeholder="e.g. 20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason / Note *</label>
                <textarea
                  rows="3"
                  required
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                  placeholder="Reason for token allocation (e.g. Granted per support ticket request)"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-bold disabled:opacity-50"
                >
                  {submitting ? 'Allocating...' : 'Confirm Allocation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAgentTokenRecharge;